import { Observable, ReplaySubject, of, zip } from "rxjs";
import { concatMap, map, switchMap, tap } from "rxjs/operators";

import { AssociatedMedias, AssociatedMediasResource, PushCachedValueResult, ReplaySubjectContent, SynchroMediaUpdates } from "@skinsoft/types";
import { SynchroUpHandler } from "@skinsoft/classes-interfaces-definition";

/**
 * @type T type of entity stored inside CACHE-UP
 * @type U type of entity pushed to backend
 * @type A type of after push additionnalData defaults to any
 */
export abstract class AbstractSyncUpHandler<T, U, A = any> implements SynchroUpHandler {
    protected cancelAsked: boolean = false
    protected locale: string | undefined = undefined
    
    abstract getNbCachedObjects(countLocallyCreated?: boolean | undefined): Promise<number> 
    abstract getNbLocallyCreatedEntities(): Promise<number>

    async handleSynchroUpdateFromCache(pushNewEntities: boolean = true, locale?: string): Promise<Observable<boolean>> {
        this.cancelAsked = false
        if(locale) {
            this.locale = locale
        }

        return this.getAllCachedEntities(pushNewEntities).pipe(
            switchMap(cachedEntities => {
                const dataToUpdate: SynchroMediaUpdates<U>[] = []

                for(const cachedEntity of cachedEntities) {
                    if(!this.cancelAsked) {
                        const formattedEntity = this.formatDataBeforePush(cachedEntity)
    
                        const localAssociatedMedias = this.getLocalAssociatedMedias(formattedEntity)
                        const localMediasKeys = this.getLocalMediasKeys(formattedEntity)
                        const mediaReplaySubjects = this.buildUploadCachedMediasReplaySubject(localMediasKeys)
    
                        const formattedEntityWithoutLocalMedias = this.removeLocalMedias(formattedEntity)
                        

                        dataToUpdate.push({ 
                            id: this.getEntityId(formattedEntity), 
                            body: formattedEntityWithoutLocalMedias, 
                            mediaSubjects: mediaReplaySubjects, 
                            cachedMedias: localAssociatedMedias as AssociatedMedias, 
                        })
                    }
                }

                return of(...dataToUpdate)
            }),
            concatMap(dataToPush => {
                return this.applyBeforePushOperations(dataToPush)
            }),
            concatMap(([beforePushRes, dataToUpdate]) => {
                let observable$: Observable<PushCachedValueResult>

                if(beforePushRes.success) {
                    if(this.isCachedCreation(dataToUpdate.id)) {
                        observable$ = this.pushCachedCreations(dataToUpdate).pipe(
                            switchMap(res => {
                                if(res.success) {
                                    if(res.newId) {
                                        this.saveMappingOldIdNewId(dataToUpdate.id, res.newId)
                                    }
                                }
                                return of(res)
                            })
                        )
                    } else {
                        observable$ = this.pushCachedUpdate(dataToUpdate)
                    }

                    return observable$.pipe(
                        switchMap(res => {
                            if(res.success) {
                                return zip(
                                    of(res),
                                    this.getAfterPushAdditionalData(dataToUpdate)
                                )
                            }

                            return zip(
                                of(res),
                                of(undefined)
                            )
                        }),
                        switchMap(([res, additionalData]) => {
                            return this.applyAfterPushOperations(res, dataToUpdate, additionalData)
                        }),
                        tap(res => {
                            if(res.success) {
                                const cacheKeysToClean = [dataToUpdate.id]
                                if(res.newId) {
                                    cacheKeysToClean.push(res.newId)
                                }
                                this.cleanCacheAfterPush(cacheKeysToClean)
                            }
                        }),
                        map(res => {
                            return res.success
                        })
                    )
                }

                return of(false)
            }),
        )
    }

    abstract getAllCachedEntities(includeCashedCreations: boolean): Observable<T[]>
    abstract formatDataBeforePush(entity: T): U
    abstract removeLocalMedias(entity: U): U
    abstract getEntityId(entity: U): string 
    abstract getLocalAssociatedMedias(entity: U): AssociatedMediasResource
    abstract getLocalMediasKeys(entity: U): string[] | null
    abstract buildUploadCachedMediasReplaySubject(localMediasKeys: string[] | null): { [replayName: string]: ReplaySubject<ReplaySubjectContent> }
    abstract applyBeforePushOperations(dataToPush: SynchroMediaUpdates<U>): Observable<[PushCachedValueResult, SynchroMediaUpdates<U>]>
    
    abstract isCachedCreation(id: string): boolean
    abstract pushCachedUpdate(dataToUpdate: SynchroMediaUpdates<U>): Observable<PushCachedValueResult>
    abstract pushCachedCreations(dataToCreate: SynchroMediaUpdates<U>): Observable<PushCachedValueResult>

    abstract saveMappingOldIdNewId(oldId: string, newId: string): void
    abstract getAfterPushAdditionalData(dataToPush: SynchroMediaUpdates<U>): Observable<A>
    abstract applyAfterPushOperations(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<U>, additionalData?: A): Observable<PushCachedValueResult> 
    abstract cleanCacheAfterPush(cacheKeys: string[]): void
    public abortPush() {
        this.cancelAsked = true
    }
}